import { apiRequest } from "../client";
import type { PaginatedResponse, SearchParams } from "../types";
import type {
  WorkerCard,
  WorkerTemplate,
  WorkerTool,
  PodTemplate,
  PodInstance,
  AgenticInteraction,
  PodInstanceActivity,
  PodInstanceChatMessage,
  PodTemplateStats,
  PodInstanceStats,
  WorkflowTask,
  Dataset,
  DatasetSummary,
  PortfolioDefinition,
  PortfolioDefinitionSummary,
  HoldingMatchRequest,
  CreatePortfolioFromDefinitionRequest,
  Activity,
  ChatMessage,
  Document,
  SystemJob,
  ChatRequest,
  DeployWorkerPayload,
  WorkerConversation,
  WorkerConversationMessage,
  CreateWorkerConversationPayload,
  SendWorkerMessagePayload,
  CallToAction,
  CallToActionCompletionRequest,
  CallToActionCompletionResponse,
} from "./types";

// Worker Cards API
export async function getWorkerCards(
  params?: SearchParams
): Promise<PaginatedResponse<WorkerCard>> {
  return apiRequest<PaginatedResponse<WorkerCard>>({
    method: "GET",
    url: "/ani-workers/cards/",
    params,
  });
}

export async function createWorkerCard(
  data: Omit<WorkerCard, "id" | "created_at" | "updated_at">
): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "POST",
    url: "/ani-workers/cards/",
    data,
  });
}

export async function getWorkerCard(workerId: string): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "GET",
    url: `/ani-workers/cards/${workerId}`,
  });
}

export async function getWorker(workerId: string): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "GET",
    url: `/ani-workers/cards/${workerId}`,
  });
}

export async function fetchWorkerCards(): Promise<WorkerCard[]> {
  return apiRequest<WorkerCard[]>({
    method: "GET",
    url: "/ani-workers/cards/",
  });
}

export async function deployWorker(
  payload: DeployWorkerPayload
): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "POST",
    url: "/ani-workers/cards/",
    data: payload,
  });
}

export async function updateWorker(
  workerId: string,
  payload: DeployWorkerPayload
): Promise<WorkerCard> {
  return apiRequest<WorkerCard>({
    method: "PUT",
    url: `/ani-workers/cards/${workerId}`,
    data: payload,
  });
}

export async function deleteWorker(workerId: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/ani-workers/cards/${workerId}`,
  });
}

// Worker Conversations API
export async function createWorkerConversation(
  data: CreateWorkerConversationPayload
): Promise<WorkerConversation> {
  return apiRequest<WorkerConversation>({
    method: "POST",
    url: "/ani-workers/conversations",
    data,
  });
}

export async function sendWorkerMessage(
  conversationId: string,
  data: SendWorkerMessagePayload,
  isTest: boolean = false,
  testWorkerName?: string,
  testWorkerInstructions?: string
): Promise<WorkerConversationMessage> {
  const params = new URLSearchParams();
  if (isTest) {
    params.append("is_test", "true");
  }
  if (testWorkerName) {
    params.append("test_worker_name", testWorkerName);
  }
  if (testWorkerInstructions) {
    params.append("test_worker_instructions", testWorkerInstructions);
  }

  return apiRequest<WorkerConversationMessage>({
    method: "POST",
    url: `/ani-workers/conversations/${conversationId}/messages?${params.toString()}`,
    data,
  });
}

// Worker Templates API
export async function getWorkerTemplates(): Promise<WorkerTemplate[]> {
  return apiRequest<WorkerTemplate[]>({
    method: "GET",
    url: "/ani-workers/templates/",
  });
}

// Worker Tools (Capabilities) API
export async function getWorkerTools(): Promise<WorkerTool[]> {
  return apiRequest<WorkerTool[]>({
    method: "GET",
    url: "/ani-workers/tools/",
  });
}

export async function cloneWorkerTool(
  toolId: string,
  payload: Pick<
    WorkerTool,
    "display_name" | "description" | "rules" | "tags" | "is_active"
  >
): Promise<WorkerTool> {
  return apiRequest<WorkerTool>({
    method: "POST",
    url: `/ani-workers/tools/${toolId}/clone`,
    data: payload,
  });
}

export async function updateWorkerTool(
  toolId: string,
  payload: Partial<WorkerTool>
): Promise<WorkerTool> {
  return apiRequest<WorkerTool>({
    method: "PUT",
    url: `/ani-workers/tools/${toolId}`,
    data: payload,
  });
}

export async function deleteWorkerTool(toolId: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/ani-workers/tools/${toolId}`,
  });
}

// Pods API
export async function getPodTemplates(
  params?: SearchParams
): Promise<PaginatedResponse<PodTemplate>> {
  return apiRequest<PaginatedResponse<PodTemplate>>({
    method: "GET",
    url: "/agentic/pod-templates/",
    params,
  });
}

export async function getPodTemplate(id: string): Promise<PodTemplate> {
  return apiRequest<PodTemplate>({
    method: "GET",
    url: `/agentic/pod-templates/${id}`,
  });
}

export async function getPodTemplateStats(
  id: string
): Promise<PodTemplateStats> {
  return apiRequest<PodTemplateStats>({
    method: "GET",
    url: `/agentic/pod-templates/${id}/stats`,
  });
}

export async function createPodTemplate(
  data: Omit<PodTemplate, "id" | "created_at" | "updated_at">
): Promise<PodTemplate> {
  return apiRequest<PodTemplate>({
    method: "POST",
    url: "/agentic/pod-templates/",
    data,
  });
}

export async function updatePodTemplate(
  id: string,
  data: Partial<PodTemplate>
): Promise<PodTemplate> {
  return apiRequest<PodTemplate>({
    method: "PUT",
    url: `/agentic/pod-templates/${id}`,
    data,
  });
}

export async function deletePodTemplate(id: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/agentic/pod-templates/${id}`,
  });
}

// ===== POD PINNING API =====

export async function pinPodTemplate(podTemplateId: string): Promise<void> {
  return apiRequest<void>({
    method: "POST",
    url: `/agentic/pins/${podTemplateId}`,
  });
}

export async function unpinPodTemplate(podTemplateId: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/agentic/pins/${podTemplateId}`,
  });
}

export async function getPinnedPodTemplateIds(): Promise<string[]> {
  return apiRequest<string[]>({
    method: "GET",
    url: "/agentic/pins/",
  });
}

export async function getPinnedPodTemplates(): Promise<PodTemplate[]> {
  return apiRequest<PodTemplate[]>({
    method: "GET",
    url: "/agentic/pod-templates/",
    params: { pinned_only: true },
  });
}

// ===== POD INSTANCES API =====

/** Raw API payloads may use `_id`, arrays, or `{ results }` / `{ data }`. */
function coercePodInstanceId(
  pod: PodInstance & { _id?: string }
): PodInstance {
  if (pod.id) return pod;
  if (pod._id) return { ...pod, id: pod._id };
  return pod;
}

function extractPodInstancesFromPayload(
  raw: unknown
): PodInstance[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((p) =>
      coercePodInstanceId(p as PodInstance & { _id?: string })
    );
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.results)) {
      return o.results.map((p) =>
        coercePodInstanceId(p as PodInstance & { _id?: string })
      );
    }
    if (Array.isArray(o.data)) {
      return o.data.map((p) =>
        coercePodInstanceId(p as PodInstance & { _id?: string })
      );
    }
    // Rare: array-like object { "0": doc, "1": doc } (some proxies / serializers)
    const keys = Object.keys(o);
    if (
      keys.length > 0 &&
      keys.every((k) => /^\d+$/.test(k)) &&
      !("results" in o) &&
      !("data" in o)
    ) {
      return keys
        .sort((a, b) => Number(a) - Number(b))
        .map((k) =>
          coercePodInstanceId(o[k] as PodInstance & { _id?: string })
        );
    }
  }
  return [];
}

export async function getPodInstances(
  params?: SearchParams & { template_id?: string }
): Promise<PaginatedResponse<PodInstance>> {
  const raw = await apiRequest<unknown>({
    method: "GET",
    url: "/agentic/pod-instances/",
    params,
  });
  const items = extractPodInstancesFromPayload(raw);
  return { results: items, count: items.length };
}

export async function getPodInstance(id: string): Promise<PodInstance> {
  const raw = await apiRequest<PodInstance & { _id?: string }>({
    method: "GET",
    url: `/agentic/pod-instances/${id}`,
  });
  return coercePodInstanceId(raw);
}

function coerceInteractionId(
  row: AgenticInteraction & { _id?: string }
): AgenticInteraction | null {
  const id = row.id ?? row._id;
  if (!id) return null;
  if (row.id === id) return row;
  return { ...row, id };
}

export async function listInteractions(params?: {
  pod_instance_id?: string;
  workflow_task_id?: string;
  state?: string;
  skip?: number;
  limit?: number;
}): Promise<AgenticInteraction[]> {
  const raw = await apiRequest<unknown>({
    method: "GET",
    url: "/agentic/interactions/",
    params,
  });
  if (!Array.isArray(raw)) {
    return [];
  }
  return (raw as (AgenticInteraction & { _id?: string })[])
    .map(coerceInteractionId)
    .filter((r): r is AgenticInteraction => r != null);
}

/** Single interaction by id (workflow state, metadata). */
export async function getAgenticInteraction(
  interactionId: string,
): Promise<AgenticInteraction> {
  const raw = await apiRequest<AgenticInteraction & { _id?: string }>({
    method: "GET",
    url: `/agentic/interactions/${interactionId}`,
  });
  const coerced = coerceInteractionId(raw);
  if (!coerced) {
    throw new Error("Invalid interaction response");
  }
  return coerced;
}

export async function getPodInstanceStats(
  id: string
): Promise<PodInstanceStats> {
  return apiRequest<PodInstanceStats>({
    method: "GET",
    url: `/agentic/pod-instances/${id}/stats`,
  });
}

export async function createPodInstance(
  data: Omit<PodInstance, "id" | "created_at" | "updated_at">
): Promise<PodInstance> {
  return apiRequest<PodInstance>({
    method: "POST",
    url: "/agentic/pod-instances/",
    data,
  });
}

export async function updatePodInstance(
  id: string,
  data: Partial<PodInstance>
): Promise<PodInstance> {
  return apiRequest<PodInstance>({
    method: "PUT",
    url: `/agentic/pod-instances/${id}`,
    data,
  });
}

export async function deletePodInstance(id: string): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/agentic/pod-instances/${id}`,
  });
}

export async function getPodInstanceActivities(
  instanceId: string
): Promise<PaginatedResponse<PodInstanceActivity>> {
  return apiRequest<PaginatedResponse<PodInstanceActivity>>({
    method: "GET",
    url: `/agentic/pod-instances/${instanceId}/activities`,
  });
}

/**
 * Retrieves workflow tasks for a pod instance or conversation
 * @param id The ID of the pod instance or conversation
 * @param options Optional configuration to specify the context type
 * @returns Array of workflow tasks associated with the context
 * @example
 * // For pod instance (default behavior for backward compatibility)
 * const tasks = await getAgenticWorkflowTasks("6824eac822847d013bd52c53");
 *
 * // For conversation (explicit)
 * const tasks = await getAgenticWorkflowTasks("conversation-id-123", { contextType: "conversation" });
 */
export async function getAgenticWorkflowTasks(
  id: string,
  options?: { contextType?: "pod_instance" | "conversation" }
): Promise<WorkflowTask[]> {
  const params =
    options?.contextType === "conversation"
      ? { conversation_id: id }
      : { pod_instance_id: id };
  return apiRequest<WorkflowTask[]>({
    method: "GET",
    url: "/agentic/workflow-tasks/",
    params,
  });
}

/**
 * Retrieves dataset summaries for a pod instance or conversation (without full data)
 * @param id The ID of the pod instance or conversation
 * @param options Optional configuration to specify the context type
 * @returns Array of dataset summaries (without the data field for efficiency)
 */
export async function getAgenticDatasetSummaries(
  id: string,
  options?: { contextType?: "pod_instance" | "conversation" }
): Promise<DatasetSummary[]> {
  const params =
    options?.contextType === "conversation"
      ? { conversation_id: id, include_data: false }
      : { pod_instance_id: id, include_data: false };
  return apiRequest<DatasetSummary[]>({
    method: "GET",
    url: "/agentic/datasets/",
    params,
  });
}

/**
 * Retrieves a single dataset by ID with full data
 * @param datasetId The ID of the dataset
 * @returns Full dataset including all data fields
 */
export async function getAgenticDataset(datasetId: string): Promise<Dataset> {
  return apiRequest<Dataset>({
    method: "GET",
    url: `/agentic/datasets/${datasetId}`,
  });
}

/**
 * Updates a single field within a dataset, preserving history
 * @param datasetId The ID of the dataset
 * @param fieldName Name of the field to update
 * @param newValue New value for the field
 * @param updatedBy Name of the user making the update
 * @param note Optional note explaining the change
 * @returns Updated dataset
 */
export async function updateAgenticDatasetField(
  datasetId: string,
  fieldName: string,
  newValue: string | number | string[] | null,
  updatedBy: string,
  note?: string
): Promise<Dataset> {
  return apiRequest<Dataset>({
    method: "PATCH",
    url: `/agentic/datasets/${datasetId}/fields`,
    data: {
      field_name: fieldName,
      new_value: newValue,
      updated_by: updatedBy,
      note: note || undefined,
    },
  });
}

/**
 * Soft-deletes a dataset by setting status to 'deleted'
 * @param datasetId The ID of the dataset to delete
 */
export async function deleteAgenticDataset(
  datasetId: string
): Promise<{ message: string }> {
  return apiRequest<{ message: string }>({
    method: "DELETE",
    url: `/agentic/datasets/${datasetId}`,
  });
}

/**
 * Retrieves datasets for a pod instance or conversation (with full data)
 * @param id The ID of the pod instance or conversation
 * @param options Optional configuration to specify the context type
 * @returns Array of datasets associated with the context
 * @deprecated Use getAgenticDatasetSummaries for listing and getAgenticDataset for details
 */
export async function getAgenticDatasets(
  id: string,
  options?: { contextType?: "pod_instance" | "conversation" }
): Promise<Dataset[]> {
  const params =
    options?.contextType === "conversation"
      ? { conversation_id: id }
      : { pod_instance_id: id };
  return apiRequest<Dataset[]>({
    method: "GET",
    url: "/agentic/datasets/",
    params,
  });
}

/**
 * Creates a new portfolio definition
 * @param data Portfolio definition data including plan details and holdings
 * @returns The created portfolio definition with server-generated fields
 * @example
 * const newPortfolio = await createAgenticPortfolioDefinition({
 *   name: "Fidelity - Investment ISA Portfolio",
 *   pod_instance_id: "pod-instance-id",
 *   workflow_task_id: "workflow-task-id",
 *   data: [
 *     {
 *       name: "plan_name",
 *       value: "Fidelity Investment ISA",
 *       confidence: 0.95,
 *       type: "text",
 *       label: "Plan Name",
 *       source_file: null,
 *       history: []
 *     },
 *     // ... other fields including portfolio_holdings
 *   ],
 *   source_files: [],
 *   metadata: {
 *     portfolio_id: "portfolio-id",
 *     original_source_files: [
 *       { file_id: "file-id", label: "Policy Information" }
 *     ]
 *   },
 *   status: "pending"
 * });
 */
export async function createAgenticPortfolioDefinition(
  data: Omit<PortfolioDefinition, "_id" | "created_at" | "updated_at">
): Promise<PortfolioDefinition> {
  return apiRequest<PortfolioDefinition>({
    method: "POST",
    url: "/agentic/portfolio-definitions/",
    data,
  });
}

// Portfolio Definitions - Get/List
export async function getAgenticPortfolioDefinitions(
  podInstanceId: string
): Promise<PortfolioDefinitionSummary[]> {
  return apiRequest<PortfolioDefinitionSummary[]>({
    method: "GET",
    url: `/agentic/portfolio-definitions`,
    params: { pod_instance_id: podInstanceId },
  });
}

/**
 * Get a single portfolio definition by ID
 */
export async function getAgenticPortfolioDefinition(
  portfolioDefId: string
): Promise<PortfolioDefinition> {
  return apiRequest<PortfolioDefinition>({
    method: "GET",
    url: `/agentic/portfolio-definitions/${portfolioDefId}`,
  });
}

/**
 * Update a portfolio definition
 */
export async function updateAgenticPortfolioDefinition(
  portfolioDefId: string,
  data: Partial<PortfolioDefinition>
): Promise<PortfolioDefinition> {
  return apiRequest<PortfolioDefinition>({
    method: "PATCH",
    url: `/agentic/portfolio-definitions/${portfolioDefId}`,
    data,
  });
}

/**
 * Match a holding to a product
 */
export async function matchAgenticPortfolioHolding(
  portfolioDefId: string,
  holdingId: string,
  matchRequest: HoldingMatchRequest
): Promise<PortfolioDefinition> {
  return apiRequest<PortfolioDefinition>({
    method: "PATCH",
    url: `/agentic/portfolio-definitions/${portfolioDefId}/holdings/${holdingId}/match`,
    data: matchRequest,
  });
}

/**
 * Clear a holding's matched product
 */
export async function clearAgenticPortfolioHoldingMatch(
  portfolioDefId: string,
  holdingId: string
): Promise<PortfolioDefinition> {
  return apiRequest<PortfolioDefinition>({
    method: "DELETE",
    url: `/agentic/portfolio-definitions/${portfolioDefId}/holdings/${holdingId}/match`,
  });
}

/**
 * Update a holding's footnote
 */
export async function updateAgenticHoldingFootnote(
  portfolioDefId: string,
  holdingId: string,
  footnote: string | null
): Promise<PortfolioDefinition> {
  return apiRequest<PortfolioDefinition>({
    method: "PATCH",
    url: `/agentic/portfolio-definitions/${portfolioDefId}/holdings/${holdingId}/footnote`,
    data: { footnote },
  });
}

/**
 * Delete a portfolio definition (soft delete)
 */
export async function deleteAgenticPortfolioDefinition(
  portfolioDefId: string
): Promise<void> {
  return apiRequest<void>({
    method: "DELETE",
    url: `/agentic/portfolio-definitions/${portfolioDefId}`,
  });
}

/**
 * Create a portfolio from a portfolio definition
 */
export async function createPortfolioFromDefinition(
  portfolioDefId: string,
  request?: CreatePortfolioFromDefinitionRequest
): Promise<PortfolioDefinition> {
  return apiRequest<PortfolioDefinition>({
    method: "POST",
    url: `/agentic/portfolio-definitions/${portfolioDefId}/create-portfolio`,
    data: request || {},
  });
}

/**
 * Retrieves all agentic activities, not specific to a single pod instance.
 * @param params Optional search parameters like limit, offset.
 * @returns Paginated list of agentic activities.
 */
export async function getAllAgenticActivities(
  params?: SearchParams
): Promise<PaginatedResponse<Activity>> {
  return apiRequest<PaginatedResponse<Activity>>({
    method: "GET",
    url: "/agentic/activities/",
    params,
  });
}

/**
 * Retrieves activities for a specific pod instance
 * @param podInstanceId The ID of the pod instance
 * @returns Array of activities associated with the pod instance
 * @example
 * const activities = await getAgenticActivities("pod-instance-id");
 */
export async function getAgenticActivities(
  podInstanceId: string
): Promise<Activity[]> {
  return apiRequest<Activity[]>({
    method: "GET",
    url: "/agentic/activities/",
    params: { pod_instance_id: podInstanceId },
  });
}

/**
 * Creates a new activity for a pod instance
 * @param data Activity data including pod instance ID, workflow task ID, and activity details
 * @returns The created activity with server-generated fields
 * @example
 * const newActivity = await createActivity({
 *   pod_instance_id: "pod-instance-id",
 *   workflow_task_id: "workflow-task-id",
 *   activity_type: "initialisation",
 *   description: "Ready to process client Fact Find and generate LoAs",
 *   status: "success",
 *   metadata: {},
 *   assignee: {
 *     id: "worker-id",
 *     type: "worker",
 *     name: "LoA Manager"
 *   }
 * });
 */
export async function createAgenticActivity(
  data: Omit<Activity, "_id" | "created_at" | "updated_at" | "timestamp">
): Promise<Activity> {
  return apiRequest<Activity>({
    method: "POST",
    url: "/agentic/activities/",
    data,
  });
}

/**
 * Retrieves chat messages for a specific pod instance
 * @param podInstanceId The ID of the pod instance to get chat messages for
 * @returns Array of chat messages associated with the pod instance
 * @example
 * const messages = await getAgenticChatMessages("6824eac822847d013bd52c53");
 */
export async function getAgenticChatMessages(
  podInstanceId: string
): Promise<ChatMessage[]> {
  return apiRequest<ChatMessage[]>({
    method: "GET",
    url: "/agentic/chat-messages/",
    params: { pod_instance_id: podInstanceId },
  });
}

/**
 * Context-agnostic chat-message fetch used by the agent-testing UI.
 *
 * Each of the three context types routes to the right backend:
 * - ``pod`` -> /agentic/chat-messages/?pod_instance_id=
 * - ``personal_worker`` -> /agentic/chat-messages/?conversation_id= (ops
 *   path; the user-scoped `/ani-workers/conversations/{id}/messages`
 *   rejects non-owners so it can't be used from ops tooling)
 * - ``in_app_agent`` -> /agentic/chat-messages/?conversation_id=
 *   (chat_messages rows for in-app use the fe_session_id as conversation_id)
 */
export async function getContextChatMessages(
  contextType: "pod" | "personal_worker" | "in_app_agent",
  contextId: string,
): Promise<ChatMessage[]> {
  const params =
    contextType === "pod"
      ? { pod_instance_id: contextId }
      : { conversation_id: contextId };
  return apiRequest<ChatMessage[]>({
    method: "GET",
    url: "/agentic/chat-messages/",
    params,
  });
}

/**
 * Context-agnostic interaction list for the agent-testing workload view.
 *
 * For ``pod`` we hit the real `/agentic/interactions/` endpoint which
 * reads `pods.interactions` (pod-centric by design). For the other two
 * types `pods.interactions` may not exist, so we synthesize a minimal
 * ``AgenticInteraction`` per distinct ``interaction_id`` present on the
 * context's chat messages — enough for the workload timeline to render
 * step numbers, statuses, and a trigger preview.
 */
export async function listContextInteractions(
  contextType: "pod" | "personal_worker" | "in_app_agent",
  contextId: string,
): Promise<AgenticInteraction[]> {
  if (contextType === "pod") {
    return listInteractions({ pod_instance_id: contextId, limit: 100 });
  }
  const messages = await getContextChatMessages(contextType, contextId);
  return synthesizeInteractionsFromMessages(messages);
}

/** Drop A/B shadow + agent-test traffic so it never shows in synthetic timelines. */
function isAgentTestOrShadowChatMessage(m: ChatMessage): boolean {
  const x = m as ChatMessage & {
    is_ab_shadow?: boolean;
    metadata?: { is_test?: boolean; ab_test_id?: string; source?: string };
  };
  if (x.is_ab_shadow === true) {
    return true;
  }
  const meta = x.metadata;
  if (meta?.is_test === true) {
    return true;
  }
  if (meta?.ab_test_id) {
    return true;
  }
  const s = meta?.source;
  if (s === 'agent_ab_test' || s === 'agent_test_runner') {
    return true;
  }
  return false;
}

function synthesizeInteractionsFromMessages(
  messages: ChatMessage[],
): AgenticInteraction[] {
  const byId = new Map<string, AgenticInteraction>();
  for (const m of messages) {
    if (isAgentTestOrShadowChatMessage(m)) {
      continue;
    }
    const iid = (m as ChatMessage & { interaction_id?: string | null })
      .interaction_id;
    if (!iid) continue;
    const createdAt =
      (m as ChatMessage & { timestamp?: string }).timestamp ??
      m.created_at ??
      new Date().toISOString();
    const existing = byId.get(iid);
    if (!existing) {
      const userContent =
        m.sender?.type === "user" || m.message_type === "user_message"
          ? m.content
          : undefined;
      byId.set(iid, {
        id: iid,
        created_at: createdAt,
        status: { state: "completed" },
        metadata: {
          message_content: userContent,
        },
      } as unknown as AgenticInteraction);
    } else {
      // Prefer the earliest user message as the trigger preview.
      const sender = m.sender?.type;
      if (
        (sender === "user" || m.message_type === "user_message") &&
        !existing.metadata?.message_content &&
        typeof m.content === "string"
      ) {
        existing.metadata = {
          ...existing.metadata,
          message_content: m.content,
        };
      }
      if (new Date(createdAt) < new Date(existing.created_at)) {
        existing.created_at = createdAt;
      }
    }
  }
  return Array.from(byId.values()).sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
}

/**
 * Retrieves documents associated with a pod instance or conversation
 * @param id The ID of the pod instance or conversation
 * @param options Optional configuration to specify the context type
 * @returns Array of documents associated with the context
 * @example
 * // For pod instance (default behavior for backward compatibility)
 * const documents = await getAgenticDocuments("6824eac822847d013bd52c53");
 *
 * // For conversation (explicit)
 * const documents = await getAgenticDocuments("conversation-id-123", { contextType: "conversation" });
 */
export async function getAgenticDocuments(
  id: string,
  options?: { contextType?: "pod_instance" | "conversation" }
): Promise<Document[]> {
  const params =
    options?.contextType === "conversation"
      ? { conversation_id: id }
      : { pod_instance_id: id };
  return apiRequest<Document[]>({
    method: "GET",
    url: "/agentic/documents/",
    params,
  });
}

/**
 * Creates a new document
 * @param data Document data including pod instance ID, file information, and uploader details
 * @returns The created document with server-generated fields
 * @example
 * const newDocument = await createAgenticDocument({
 *   pod_instance_id: "pod-instance-id",
 *   workflow_task_id: "workflow-task-id",
 *   original_filename: "Scottish_Widows_SIPP_LoA.pdf",
 *   display_name: "Scottish Widows - SIPP LoA",
 *   mime_type: "application/pdf",
 *   size: 0,
 *   storage_path: "development/pod-instance-id/SW_SIPP_LoA.pdf",
 *   render_url: "/pdf?config=cfg-id&template=letter-of-authority",
 *   is_output: true,
 *   source_files: [],
 *   metadata: {},
 *   uploaded_by: {
 *     id: "worker-id",
 *     type: "worker",
 *     name: "LoA Manager"
 *   }
 * });
 */
export async function createAgenticDocument(
  data: Omit<Document, "_id" | "uploaded_at" | "created_at" | "updated_at">
): Promise<Document> {
  return apiRequest<Document>({
    method: "POST",
    url: "/agentic/documents",
    data,
  });
}

/**
 * Creates a new chat message
 * @param data Chat message data including pod instance ID, content, and sender information
 * @returns The created chat message with server-generated fields
 * @example
 * const newMessage = await createChatMessage({
 *   pod_instance_id: "pod-instance-id",
 *   workflow_task_id: "workflow-task-id",
 *   content: "👋 Hi there! I'm Hermione...",
 *   message_type: "chat",
 *   actions: [{ type: "upload", label: "Upload Fact Find" }],
 *   sender: {
 *     id: "worker-id",
 *     type: "worker",
 *     name: "LoA Manager"
 *   }
 * });
 */
export async function createAgenticChatMessage(
  data: Omit<ChatMessage, "_id" | "timestamp" | "created_at" | "updated_at">
): Promise<ChatMessage> {
  return apiRequest<ChatMessage>({
    method: "POST",
    url: "/agentic/chat-messages/",
    data,
  });
}

/**
 * Fetches all system jobs for a given pod instance or conversation
 * @param targetId The ID of the pod instance or conversation to get system jobs for
 * @param isConversation Whether the targetId is a conversation ID (default: false)
 * @returns Array of system jobs associated with the pod instance or conversation
 * @example
 * const jobs = await getAgenticSystemJobs("6824eac822847d013bd52c53");
 * const conversationJobs = await getAgenticSystemJobs("conv-123", true);
 */
export async function getAgenticSystemJobs(
  targetId: string,
  isConversation: boolean = false
): Promise<SystemJob[]> {
  const params = isConversation
    ? { conversation_id: targetId }
    : { pod_instance_id: targetId };

  return apiRequest<SystemJob[]>({
    method: "GET",
    url: "/agentic/system-jobs/",
    params,
  });
}

/**
 * Creates a new system job
 * @param data System job data including pod instance ID, job type, and job details
 * @returns The created system job with server-generated fields
 * @example
 * const newJob = await createSystemJob({
 *   pod_instance_id: "pod-instance-id",
 *   workflow_task_id: "workflow-task-id",
 *   type: "generate_loa_standard_life",
 *   description: "Create LoA for Standard Life SIPP",
 *   status: "completed",
 *   priority: 2,
 *   dependencies: [],
 *   attempts: 1,
 *   max_attempts: 3,
 *   input: {
 *     provider: "Standard Life",
 *     scheme_type: "SIPP",
 *     template_id: "tpl-sl-sipp-001"
 *   },
 *   output: {
 *     status: "success",
 *     loa_reference: "SL-SIPP-20250508-001"
 *   },
 *   completed_at: "2025-05-08T09:45:15.000Z"
 * });
 */
export async function createAgenticSystemJob(
  data: Omit<SystemJob, "_id" | "created_at" | "updated_at">
): Promise<SystemJob> {
  return apiRequest<SystemJob>({
    method: "POST",
    url: "/agentic/system-jobs/",
    data,
  });
}

/**
 * Uploads a document file for an agentic pod instance or personal worker conversation.
 * The file is sent as multipart/form-data, with pod_instance_id or conversation_id as a separate field
 * and other document attributes sent as a JSON string under the 'metadata' field.
 *
 * @param file The file to upload.
 * @param documentAttributes Attributes for the document, excluding server-set fields
 *                           (e.g., _id, timestamps, file-derived info like size/mime_type, storage_path).
 *                           Must include either `pod_instance_id` or `conversation_id`.
 * @returns The created document record from the server.
 * @example
 * // For pod context
 * const newDocument = await uploadAgenticDocument(
 *   myFile,
 *   {
 *     pod_instance_id: "pod-instance-id-123",
 *     display_name: "Client Photo ID.jpg",
 *     workflow_task_id: "task-id-456",
 *     is_output: false,
 *     metadata: { custom_key: "custom_value" },
 *     uploaded_by: { id: "user-id-789", type: "user", name: "John Doe" }
 *   }
 * );
 *
 * // For personal worker context
 * const newDocument = await uploadAgenticDocument(
 *   myFile,
 *   {
 *     conversation_id: "conversation-id-456",
 *     display_name: "Client Photo ID.jpg",
 *     is_output: false,
 *     metadata: { custom_key: "custom_value" },
 *     uploaded_by: { id: "user-id-789", type: "user", name: "John Doe" }
 *   }
 * );
 */
export async function uploadAgenticDocument(
  file: File,
  documentAttributes: Omit<
    Document,
    | "_id"
    | "created_at"
    | "updated_at"
    | "uploaded_at"
    | "size"
    | "mime_type"
    | "original_filename"
    | "storage_path"
    | "render_url"
  >
): Promise<Document> {
  const formData = new FormData();
  formData.append("file", file);

  // Extract context fields and send them as separate fields
  const { pod_instance_id, conversation_id, ...otherAttributes } =
    documentAttributes;

  if (pod_instance_id) {
    formData.append("pod_instance_id", pod_instance_id);
  }
  if (conversation_id) {
    formData.append("conversation_id", conversation_id);
  }

  formData.append("metadata", JSON.stringify(otherAttributes));

  return apiRequest<Document>({
    method: "POST",
    url: "/agentic/documents/upload",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

export async function sendPodChatMessage(
  analysisId: string,
  message: string,
  conversationHistory: ChatMessage[] = []
) {
  return apiRequest<ChatMessage>({
    method: "POST",
    url: "/agentic/chat-messages/",
    data: {
      pod_instance_id: analysisId,
      workflow_task_id: "instance_chat",
      content: message,
      message_type: "chat",
      actions: [],
      sender: {
        id: "current-user",
        type: "user",
        name: "You",
      },
      conversation_history: conversationHistory,
    },
  });
}

/**
 * Complete a call-to-action with the provided response data
 * @param ctaId The ID of the call-to-action to complete
 * @param request The completion request data based on the remedy type
 * @returns The completion response with success status and next action
 * @example
 * // Text input completion
 * const response = await completeCallToAction("cta-123", {
 *   remedy_type: "text_input",
 *   value: "Policy number ABC123"
 * });
 *
 * // Button action completion
 * const response = await completeCallToAction("cta-456", {
 *   remedy_type: "button_action",
 *   button_action: "approve",
 *   comment: "Looks good to proceed"
 * });
 *
 * // Document upload completion
 * const response = await completeCallToAction("cta-789", {
 *   remedy_type: "document_upload",
 *   file: fileObject,
 *   description: "Latest pension statement"
 * });
 */
export async function completeCallToAction(
  ctaId: string,
  request: CallToActionCompletionRequest
): Promise<CallToActionCompletionResponse> {
  // Handle file uploads differently
  if (request.remedy_type === "document_upload") {
    const formData = new FormData();
    formData.append("response_data[remedy_type]", request.remedy_type);
    formData.append("file", request.file);
    if (request.description) {
      formData.append("response_data[description]", request.description);
    }

    return apiRequest<CallToActionCompletionResponse>({
      method: "POST",
      url: `/agentic/call-to-actions/${ctaId}/complete`,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  // Handle all other request types as JSON
  return apiRequest<CallToActionCompletionResponse>({
    method: "POST",
    url: `/agentic/call-to-actions/${ctaId}/complete`,
    data: {
      response_data: request,
    },
  });
}

/**
 * Get call-to-actions for a specific template (all instances)
 * @param templateId The ID of the pod template
 * @param options Optional filtering parameters
 * @returns Promise<CallToAction[]>
 *
 * @example
 * const ctas = await getCallToActionsByTemplate("template-123", { status: "live" });
 */
export async function getCallToActionsByTemplate(
  templateId: string,
  options?: {
    status?: string;
    category?: string;
    priority?: string;
    skip?: number;
    limit?: number;
  }
): Promise<CallToAction[]> {
  const params: Record<string, string> = {
    template_id: templateId,
  };

  if (options?.status) params.status = options.status;
  if (options?.category) params.category = options.category;
  if (options?.priority) params.priority = options.priority;
  if (options?.skip !== undefined) params.skip = options.skip.toString();
  if (options?.limit !== undefined) params.limit = options.limit.toString();

  return apiRequest<CallToAction[]>({
    method: "GET",
    url: "/agentic/call-to-actions/",
    params,
  });
}

/**
 * Get all call-to-actions (optionally filtered)
 * @param options Optional filtering parameters
 * @returns Promise<CallToAction[]>
 */
export async function getAllCallToActions(
  options?: {
    status?: string;
    category?: string;
    priority?: string;
    skip?: number;
    limit?: number;
  }
): Promise<CallToAction[]> {
  const params: Record<string, string> = {};

  if (options?.status) params.status = options.status;
  if (options?.category) params.category = options.category;
  if (options?.priority) params.priority = options.priority;
  if (options?.skip !== undefined) params.skip = options.skip.toString();
  if (options?.limit !== undefined) params.limit = options.limit.toString();

  return apiRequest<CallToAction[]>({
    method: "GET",
    url: "/agentic/call-to-actions/",
    params,
  });
}

/**
 * Get active call-to-actions for a specific template
 * @param templateId The ID of the pod template
 * @returns Promise<CallToAction[]>
 *
 * @example
 * const activeCtas = await getActiveCallToActionsByTemplate("template-123");
 */
export async function getActiveCallToActionsByTemplate(
  templateId: string
): Promise<CallToAction[]> {
  return getCallToActionsByTemplate(templateId, { status: "live", limit: 500 });
}

/**
 * Cancel a call-to-action (mark as cancelled/dismissed)
 * @param ctaId The ID of the call-to-action to cancel
 * @returns Promise<CallToAction>
 *
 * @example
 * const cancelledCta = await cancelCallToAction("cta-123");
 */
export async function cancelCallToAction(ctaId: string): Promise<CallToAction> {
  return apiRequest<CallToAction>({
    method: "POST",
    url: `/agentic/call-to-actions/${ctaId}/cancel`,
  });
}

export async function getPodInstanceWorkflowStages(
  podInstanceId: string
): Promise<any[]> {
  return apiRequest<any[]>({
    method: "GET",
    url: `/agentic/workflow-tasks/pod-instance/${podInstanceId}/workflow`,
  });
}

// Document download
export async function downloadAgenticDocument(
  documentId: string
): Promise<Blob> {
  return apiRequest<Blob>({
    method: "GET",
    url: `/agentic/documents/${documentId}/download`,
    responseType: "blob",
  });
}

/**
 * Fetches all system jobs for ops monitoring with advanced filtering
 * For 'ani' company users, allows higher limits for comprehensive monitoring
 * @param params Optional parameters for filtering system jobs
 * @returns Array of system jobs with optional filtering
 */
export async function getOpsSystemJobs(params?: {
  status?: string;
  type?: string;
  skip?: number;
  limit?: number;
  pod_instance_id?: string;
  conversation_id?: string;
  workflow_task_id?: string;
}): Promise<SystemJob[]> {
  // For ops monitoring, request more data if no limit specified
  const opsParams = {
    ...params,
    limit: params?.limit || 500, // Default to 500 for ops monitoring (ani users can handle this)
  };

  return apiRequest<SystemJob[]>({
    method: "GET",
    url: "/agentic/system-jobs/",
    params: opsParams,
  });
}

/**
 * Updates a system job with new data (e.g., marking as failed)
 * @param jobId The ID of the system job to update
 * @param data The update data
 * @returns Updated system job
 */
export async function updateSystemJob(
  jobId: string,
  data: Partial<SystemJob>
): Promise<SystemJob> {
  return apiRequest<SystemJob>({
    method: "PUT",
    url: `/agentic/system-jobs/${jobId}`,
    data,
  });
}

/**
 * Mark a system job as failed - Ops only endpoint
 * @param jobId The ID of the system job to mark as failed
 * @returns Updated system job
 */
export async function markSystemJobFailedForOps(
  jobId: string
): Promise<SystemJob> {
  return apiRequest<SystemJob>({
    method: "PATCH",
    url: `/agentic/system-jobs/${jobId}/mark-failed`,
  });
}

export async function getWorkflowStats(
  ownerId?: string,
  templateId?: string
): Promise<{ stats: any[] }> {
  const params: Record<string, string> = {};
  if (ownerId && ownerId !== "all") {
    params.owner_id = ownerId;
  }
  if (templateId) {
    params.template_id = templateId;
  }
  return apiRequest<{ stats: any[] }>({
    method: "GET",
    url: "/agentic/workflow-tasks/stats",
    params,
  });
}
